import { setPageMeta } from '../seo'
import React, { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import SiteFooter from '../components/SiteFooter'

// The film's own page. In pre-production, so no trailer and no footage. The
// words are Elaine's, used as written. Centred throughout: there is not enough
// on the page yet for a left-aligned column to hold it together.

// Hillary has her own page, so her square opens it in a window over this one.
// The other three open a bio in the same Lightbox the home-page cast grid uses.
const team = [
  {
    name: 'Hillary O\u2019Sullivan',
    role: 'Pianist',
    image: 'portrait-hillary-osullivan.jpg',
    href: '/hillary-osullivan',
  },
  {
    name: 'Elaine Lim',
    role: 'Co-Director, Producer',
    image: 'portrait-elaine-lim.jpg',
    link: 'https://www.whiteroomstudio.com.sg/',
    content: (
      <>
        <p>Elaine Lim is a Singapore-based photographer and filmmaker whose work brings classical piano, photography, and conservation into the same frame.</p>
        <p>Elaine discovered her love for photography while studying in Australia, eventually winning photography awards there and in the United States. She returned to Singapore to launch White Room Studio in 2009, a portrait photography studio that has since hosted more than 19,000 sessions and built a multi-generational community of returning clients.</p>
        <p>Elaine directed and produced the documentary The Girl and Her Piano, which was broadcast nationally on PBS and received festival awards from Los Angeles to Bhutan. Her current project, Hands Like Ours, brings a piano into the Sumatran rainforest for the orangutans and the forest they call home. Driven by the belief that orangutans share a common fate with humanity, Elaine makes films to say what is urgent for everyone who calls Earth home.</p>
      </>
    ),
  },
  {
    name: 'Ian Mun',
    role: 'Co-Director, Director of Photography, Editor',
    image: 'portrait-ian-mun.jpg',
    link: 'https://therovingstudio.co/',
    content: (
      <>
        <p>Ian is a Singapore-based wildlife and conservation storyteller. As a filmmaker and a photographer, he weaves tales that give life to untold stories, bridging the gap between humanity and nature. With every narrative, he seeks to celebrate our planet, one story at a time. In 2021, he founded The Roving Studio, a creative space dedicated to unearthing stories where communities, culture, climate, and the natural world converge.</p>
      </>
    ),
  },
  {
    name: 'Nanang Sujana',
    role: 'Cinematographer',
    image: 'portrait-nanang-sujana.jpg',
    link: 'https://nanangsujana.id/',
    content: (
      <>
        <p>Nanang Sujana is an award-winning documentary filmmaker whose work spans directing, filming and editing films on the environment, indigenous peoples and social issues. He has worked on news features and programming for BBC Natural History, Netflix, National Geographic, The Guardian, HBO, PBS, Al Jazeera, Stern TV and other major broadcasters. He is a board member and principal filmmaker of the Indonesia Nature Film Society (INFIS).</p>
        <p>With his deep knowledge of Indonesian nature and culture, Nanang became the first Indonesian to win an international award in nature and wildlife filmmaking. In 2008, in collaboration with Handcrafted Films, he won Best Cultural Message at the 31st International Wildlife Film Festival in Montana. In 2009 he received the Best Asia-Oceania Newcomer Award at the 9th Japan Wildlife Film Festival, the largest wildlife film festival in Asia.</p>
        <p>He has trained hundreds of video makers across Indonesia and in Malaysia, the Philippines and Papua New Guinea.</p>
      </>
    ),
  },
]

// The three with a bio, in grid order. The Lightbox index counts these only.
const bios = team.filter((c) => c.content)

// Same markup as PhotoWithContent on the home page, so the existing
// .photo-with-content styles carry it.
const CrewSlide = ({ slide, isMobile }) => (
  <div className={`photo-with-content ${isMobile ? 'mobile' : ''}`}>
    <div className='photo-with-content-grid'>
      <div className='photo-with-content-photo'>
        <img src={`/assets/hlo/${slide.image}`} alt={slide.name} />
      </div>
      <div className='photo-with-content-text'>
        <h1>{slide.name}</h1>
        <div>
          {slide.content}
          {slide.link && (
            <div className='link'>
              <a href={slide.link} target='_blank' rel='noopener noreferrer'>{slide.link}</a>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)

// The figures from the deck, rebuilt as type rather than a picture of a slide.
const figures = [
  {
    value: '~11,700',
    label: 'Sumatran orangutans remaining, a decline of 19.5% since 2016',
  },
  {
    value: '~700',
    label: 'Tapanuli orangutans, the rarest great ape on earth. An estimated 58, about 7% of the species, were killed by landslides in November 2025',
  },
  {
    value: '~15bn',
    label: 'tonnes of carbon held in the peat beneath the Sumatran forest floor',
  },
]

// The organisations the film is made with. Each logo links to its site. The
// logos are shown as supplied, in their own colours, straight on the green;
// recolouring a partner's mark is theirs to approve, not ours. Add a funding
// partner as a second group above this one when there is a signed one.
const partners = [
  {
    label: 'In collaboration with',
    items: [
      { name: 'Yayasan Ekosistem Lestari (YEL)', image: 'logo-yel.png', href: 'https://www.yel.or.id/' },
      { name: 'Orangutan Haven', image: 'logo-orangutan-haven.png', href: 'https://orangutanhaven.com/' },
      { name: 'Sumatran Orangutan Conservation Programme (SOCP)', image: 'logo-socp.png', href: 'https://sumatranorangutan.org/' },
    ],
  },
]

const HandsLikeOurs = () => {
  const { isMobile } = useOutletContext()
  const [bioIndex, setBioIndex] = useState(-1)
  // Hillary's page, opened in a window over this one. The link underneath is a
  // real link to /hillary-osullivan, so it still works without JavaScript.
  const [pageOpen, setPageOpen] = useState(false)

  useEffect(() => {
    if (!pageOpen) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setPageOpen(false) }
    document.body.classList.add('hlo-modal-open')
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.classList.remove('hlo-modal-open')
      window.removeEventListener('keydown', onKey)
    }
  }, [pageOpen])

  useEffect(() => {
    setPageMeta({
      title: 'Hands Like Ours | Sumatran Orangutans and Rainforests',
      path: '/handslikeours',
      description: 'Hands Like Ours is a short documentary following Singaporean pianist Hillary O\u2019Sullivan into the Leuser Ecosystem of North Sumatra, home of the critically endangered Sumatran orangutan. In pre-production for 2027.',
      image: '/assets/hlo/hands-like-ours-documentary-sumatran-orangutans.jpg',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Movie',
        name: 'Hands Like Ours',
        alternateName: 'Hands Like Ours: Orangutans in the Leuser Canopy',
        url: 'https://www.thegirlandherpiano.com/handslikeours',
                image: 'https://www.thegirlandherpiano.com/assets/hlo/hands-like-ours-documentary-sumatran-orangutans.jpg',
        description: 'A short documentary following Singaporean pianist Hillary O\u2019Sullivan into the Leuser Ecosystem of North Sumatra, the last place on earth where wild orangutans, elephants, tigers and rhinos share a forest. In pre-production; scheduled for 2027.',
        genre: ['Documentary', 'Environment'],
        inLanguage: 'en',
        countryOfOrigin: { '@type': 'Country', name: 'Singapore' },
        contentLocation: { '@type': 'Place', name: 'Leuser Ecosystem, North Sumatra, Indonesia' },
        director: [
          { '@type': 'Person', name: 'Elaine Lim' },
          { '@type': 'Person', name: 'Ian Mun' },
        ],
        producer: { '@type': 'Person', name: 'Elaine Lim' },
        editor: { '@type': 'Person', name: 'Ian Mun' },
        actor: { '@type': 'Person', name: 'Hillary O\u2019Sullivan', url: 'https://www.thegirlandherpiano.com/hillary-osullivan' },
        productionCompany: [
          { '@type': 'Organization', name: 'White Room Studio Pte Ltd', url: 'https://www.whiteroomstudio.com.sg/' },
          { '@type': 'Organization', name: 'The Roving Studio', url: 'https://therovingstudio.co/' },
        ],
        contributor: partners.flatMap((g) => g.items.map((o) => ({ '@type': 'Organization', name: o.name, url: o.href }))),
        sameAs: ['https://handslikeours.com', 'https://handslikeoursfilm.com'],
      },
    })
    window.scrollTo(0, 0)

  }, [])

  return (
    <div className='App page-standard page-hlo'>
      <header
        className='hlo-hero'
        style={{ backgroundImage: "url('/assets/hlo/leuser-rainforest-canopy.jpg')" }}
      >
        <div className='hlo-hero-veil' aria-hidden='true' />
        <div className='hlo-hero-inner'>
          <img
            className='hlo-mark'
            src='/assets/hlo/hands-like-ours-mark.png'
            alt=''
            aria-hidden='true'
            width='560'
            height='560'
          />
          {/* The h1 is the longer line a search engine reads; the visible
              title stays as it is. */}
          <h1 className='visually-hidden'>Hands Like Ours: Sumatran orangutans and rainforests</h1>
          <p className='hlo-title'>Hands Like Ours</p>
          <p className='hlo-hero-sub'>Orangutans in the Leuser Canopy</p>
          <p className='hlo-status'>In pre-production</p>
        </div>
      </header>

      <main>
        <section className='hlo-logline' data-tone='dark'>
          <div className='content-container'>
            <p>
              A young pianist journeys into Sumatra with a question, bringing
              her music into the heart of the rainforest.
            </p>
          </div>
        </section>

        <section className='hlo-film' data-tone='dark'>
          <div className='content-container'>
            <p>
              Deep within the Leuser Ecosystem of North Sumatra, the wild
              orangutans that have called these forests home for a million years are facing a rapidly changing
              landscape.
            </p>
            <p>
              Hands Like Ours follows Singaporean pianist Hillary
              O&rsquo;Sullivan as she explores our relationship with these
              incredible primates, the researchers dedicating their lives to
              protecting them, and the fragile ecosystem we share. The film
              takes her music from the Orangutan Haven into the Leuser forest
              itself, bridging the distance between Singapore&rsquo;s city streets
              and the wild next door.
            </p>
          </div>
        </section>

        <section className='hlo-orang' data-tone='dark'>
          <img
            src='/assets/hlo/sumatran-orangutan-infant-band.jpg'
            alt='A young Sumatran orangutan beside its mother'
            width='1236'
            height='915'
            loading='lazy'
          />
          <p className='hlo-credit'>Photograph: Ian Mun</p>
        </section>

        <section className='hlo-fact' data-tone='dark'>
          <div className='content-container'>
            <h2>The Leuser Ecosystem</h2>
            <p>
              The last place on Earth where wild orangutans, elephants, tigers
              and rhinos still co-exist in a single shared forest.
              <span>The nearest wild orangutans to Singapore.</span>
            </p>
          </div>
        </section>

        <section className='hlo-figures' data-tone='dark'>
          <div className='content-container'>
            <ul className='hlo-figure-list'>
              {figures.map((f) => (
                <li key={f.value}>
                  <span className='hlo-figure-value'>{f.value}</span>
                  <span className='hlo-figure-label'>{f.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          className='hlo-forest hlo-forest--piano'
          data-tone='dark'
          style={{ backgroundImage: "url('/assets/hlo/grand-piano-against-forest.jpg')" }}
        >
          <p className='hlo-credit'>Photograph: Elaine Lim</p>
        </section>

        <section className='hlo-why' data-tone='dark'>
          <div className='content-container'>
            <h2>Director&rsquo;s vision</h2>
            <blockquote>
              <p>
                This project is about a shared emotional inheritance. The forest
                is where all of us come from, and the orangutans are the part of
                that family that never left it. Singapore sits just two
                generations away from our own history of plantations and primary
                jungle, a memory our modern city has largely left behind.
              </p>
              <p>
                By bringing music directly into the Leuser Ecosystem,
                we want to collapse the distance between Singapore&rsquo;s urban
                landscape and the frontline of wildlife conservation. The
                Sumatran rainforest is close enough to be called our backyard,
                and our goal is to show why it must be cared for like home.
              </p>
            </blockquote>
            <cite>Elaine Lim, Co-Director, Producer</cite>
          </div>
        </section>

        {/* A frame from the recce: the Leuser canopy from the Bukit Lawang
            side, the town in the haze beyond it. A pause between the words
            and the people. */}
        <section
          className='hlo-forest hlo-forest--recce'
          data-tone='dark'
          style={{ backgroundImage: "url('/assets/hlo/recce/recce-leuser-canopy-bukit-lawang.jpg')" }}
          role='img'
          aria-label='The Leuser rainforest canopy above Bukit Lawang, the town in the haze beyond'
        >
          <p className='hlo-credit'>Photograph: Ian Mun</p>
        </section>

        {/* The team and the partners: one section, quiet and small. Portraits
            at a modest size, the partner logos in a single row under them. */}
        <section className='hlo-people' data-tone='dark'>
          <div className='hlo-wrap'>
            <h2 className='hlo-head'>The Team &amp; Partners</h2>
            <ul className='hlo-people-grid'>
              {team.map((c) => {
                const inner = (
                  <>
                    <span className='hlo-people-photo'>
                      <img src={`/assets/hlo/${c.image}`} alt={c.name} width='560' height='560' loading='lazy' />
                    </span>
                    <span className='hlo-people-name'>{c.name}</span>
                    <span className='hlo-people-role'>{c.role}</span>
                    <span className='hlo-people-more'>{c.href ? 'Her page' : 'Bio'}</span>
                  </>
                )
                return (
                  <li key={c.name}>
                    {c.href ? (
                      <a
                        className='hlo-people-card'
                        href={c.href}
                        onClick={(e) => { e.preventDefault(); setPageOpen(true) }}
                      >
                        {inner}
                      </a>
                    ) : (
                      <button
                        type='button'
                        className='hlo-people-card'
                        onClick={() => setBioIndex(bios.indexOf(c))}
                      >
                        {inner}
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
            {pageOpen && (
              <div
                className='hlo-page-modal'
                role='dialog'
                aria-modal='true'
                aria-label='Hillary O\u2019Sullivan'
                onClick={() => setPageOpen(false)}
              >
                <div className='hlo-page-modal-window' onClick={(e) => e.stopPropagation()}>
                  <button
                    type='button'
                    className='hlo-page-modal-close'
                    aria-label='Close'
                    onClick={() => setPageOpen(false)}
                  >
                    &times;
                  </button>
                  <iframe src='/hillary-osullivan' title='Hillary O\u2019Sullivan' />
                </div>
              </div>
            )}
            <Lightbox
              open={bioIndex >= 0}
              index={bioIndex}
              close={() => setBioIndex(-1)}
              slides={bios.map((c) => ({ ...c, type: 'custom-slide', src: `/assets/hlo/${c.image}` }))}
              render={{ slide: ({ slide }) => <CrewSlide slide={slide} isMobile={isMobile} /> }}
            />

            <div className='hlo-with'>
              <p className='hlo-with-label'>In collaboration with</p>
              <ul className='hlo-with-list'>
                {partners[0].items.map((o) => (
                  <li key={o.name}>
                    <a href={o.href} target='_blank' rel='noopener noreferrer' aria-label={`${o.name}, opens in a new tab`} title={o.name}>
                      <img src={`/assets/hlo/partners/${o.image}`} alt={o.name} loading='lazy' />
                    </a>
                  </li>
                ))}
              </ul>
              <p className='hlo-with-credit'>
                Produced by <a href='https://www.whiteroomstudio.com.sg/' target='_blank' rel='noopener noreferrer'>White Room Studio</a>, Singapore.
                Co-directed with <a href='https://therovingstudio.co/' target='_blank' rel='noopener noreferrer'>The Roving Studio</a>.
              </p>
            </div>
          </div>
        </section>

        {/* The diary and the chapters: two cards side by side, a photograph
            each at a modest size, the name, one line, one link. On a phone
            they stack. The chapter pages are served by hlo.php, not this app. */}
        <section className='hlo-read' data-tone='dark'>
          <div className='hlo-read-grid'>
            <article className='hlo-card'>
              <a className='hlo-card-photo' href='/handslikeours/producers-diary' tabIndex='-1' aria-hidden='true'>
                <img src='/assets/hlo/recce/recce-ian-mun-orangutan-haven.jpg' alt='' width='1000' height='1000' loading='lazy' />
              </a>
              <p className='hlo-card-kicker'>Notes from the rainforest</p>
              <h2 className='hlo-card-title'><a href='/handslikeours/producers-diary'>Producer&rsquo;s Diary</a></h2>
              <p className='hlo-card-desc'>
                What happens behind the camera: the days in the forest, and the
                people and orangutans we meet, written on the road.
              </p>
              <a className='hlo-card-link' href='/handslikeours/producers-diary'>Read the diary</a>
            </article>

            <article className='hlo-card'>
              <a className='hlo-card-photo' href='/handslikeours/chapters/disagreeably-human' tabIndex='-1' aria-hidden='true'>
                <img src='/assets/hlo/chapters/person-of-the-forest/orangutan-looking-down-card.jpg' alt='' width='1200' height='800' loading='lazy' />
              </a>
              <p className='hlo-card-kicker'>Chapter 1 &middot; Disagreeably Human</p>
              <h2 className='hlo-card-title'><a href='/handslikeours/chapters'>Chapters</a></h2>
              <p className='hlo-card-desc'>
                The story behind the film, in short chapters: the orangutan,
                its rainforest, and why what happens there reaches all of us.
              </p>
              <p className='hlo-card-actions'>
                <a className='hlo-card-link' href='/handslikeours/chapters/disagreeably-human'>Read Chapter 1</a>
                <a className='hlo-card-more' href='/handslikeours/chapters'>All Chapters</a>
              </p>
            </article>
          </div>
        </section>

        {/* The closing line, full width over the forest, set like a film's
            last card. A p, not an h2: it is the last thing said, not a heading. */}
        <section
          className='hlo-closing'
          data-tone='dark'
          style={{ backgroundImage: "url('/assets/hlo/leuser-rainforest-canopy.jpg')" }}
        >
          <div className='hlo-closing-veil' aria-hidden='true' />
          <p className='hlo-closing-line'>
            <span className='hlo-closing-kicker'>The forests are</span>
            <span className='hlo-closing-main'>our home too</span>
          </p>
        </section>
      </main>

      <SiteFooter
        mark='/assets/hlo/hands-like-ours-mark.png'
        markHover='/assets/hlo/hands-like-ours-mark.png'
        film='hlo'
      />
    </div>
  )
}

export default HandsLikeOurs
